import {RndDragCallback, RndResizeCallback} from 'react-rnd';
import {useRef} from 'react';

export type TRect = {x: number; y: number; width: number; height: number};

const loadRect = (key: string, fallback: TRect): TRect => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return fallback;
    }
    const parsed = JSON.parse(raw) as Partial<TRect>;
    if (
      typeof parsed?.x === 'number' &&
      typeof parsed?.y === 'number' &&
      typeof parsed?.width === 'number' &&
      typeof parsed?.height === 'number'
    ) {
      return parsed as TRect;
    }
  } catch {
    // ignore malformed/unavailable storage and fall back to defaults
  }
  return fallback;
};

const saveRect = (key: string, rect: TRect) => {
  try {
    localStorage.setItem(key, JSON.stringify(rect));
  } catch {
    // ignore storage errors (quota exceeded / disabled)
  }
};

export const useVideoPosition = (key: string, fallback: TRect) => {
  const rect = useRef<TRect | null>(null);

  // Loaded during the first render: a ref filled in an effect does not re-render, so a window
  // mounted later (e.g. a live stream joining mid-session) would stay hidden until another update.
  if (rect.current === null) {
    rect.current = loadRect(key, fallback);
  }

  const onDragStop: RndDragCallback = (_e, d) => {
    rect.current = {...(rect.current as TRect), x: d.x, y: d.y};
    saveRect(key, rect.current);
  };

  const onResizeStop: RndResizeCallback = (_e, _dir, ref, _delta, position) => {
    rect.current = {
      x: position.x,
      y: position.y,
      width: ref.offsetWidth,
      height: ref.offsetHeight
    };
    saveRect(key, rect.current);
  };

  const reset = () => {
    rect.current = fallback;
    saveRect(key, fallback);
  };

  return {default: rect.current, onDragStop, onResizeStop, reset};
};
